<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useMapGetter, useStore, useStoreGetters } from 'dashboard/composables/store';
import ContactSegmentsAPI from 'dashboard/api/contactSegments';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const store = useStore();
const getters = useStoreGetters();

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  selectedSegments: [],
  // trigger mode: campaign stays live and fires when a new company matches
  triggerEnabled: false,
  triggerMatchType: 'all',
  triggerConditions: [],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const segments = ref([]);

const companyFieldDefs = computed(
  () => getters['companyFieldDefinitions/getAll'].value || []
);

const COMPANY_PROPERTIES = [
  { key: 'name', label: 'Company Name' },
  { key: 'domain', label: 'Company Domain' },
  { key: 'description', label: 'Company Description' },
];

const companyPropertyOptions = computed(() => [
  ...COMPANY_PROPERTIES,
  ...companyFieldDefs.value.map(f => ({
    key: `extra.${f.field_key}`,
    label: f.label,
    fieldType: f.field_type,
    options: f.options || [],
  })),
]);

const TRIGGER_OPERATORS = [
  { value: 'is', label: 'Is' },
  { value: 'is_not', label: 'Is not' },
  { value: 'is_any_of', label: 'Is any of (comma separated)' },
  { value: 'contains', label: 'Contains' },
  { value: 'is_present', label: 'Has any value' },
  { value: 'is_not_present', label: 'Is empty' },
];

onMounted(async () => {
  store.dispatch('companyFieldDefinitions/fetchAll');
  try {
    const { data } = await ContactSegmentsAPI.get();
    segments.value = data.payload || [];
  } catch (error) {
    segments.value = [];
  }
});

const needsValue = operator =>
  !['is_present', 'is_not_present'].includes(operator);

const triggerListOptions = condition => {
  const property = companyPropertyOptions.value.find(o => o.key === condition.key);
  return property?.fieldType === 'list' && property.options?.length
    ? property.options
    : null;
};

const addTriggerCondition = () => {
  state.triggerConditions.push({
    source: 'company',
    key: 'name',
    operator: 'is',
    value: '',
  });
};

const removeTriggerCondition = index => {
  state.triggerConditions.splice(index, 1);
};

const completeTriggerConditions = computed(() =>
  state.triggerConditions.filter(
    c => c.key && c.operator && (!needsValue(c.operator) || c.value !== '')
  )
);

// audience is valid when at least one label OR segment is selected
const hasAudience = computed(
  () =>
    state.selectedAudience?.length > 0 || state.selectedSegments?.length > 0
);
// in trigger mode, recipients come from the matched company, not audience;
// instead we require at least one complete trigger condition
const audienceSatisfied = computed(() =>
  state.triggerEnabled
    ? completeTriggerConditions.value.length > 0
    : hasAudience.value
);
const submitAttempted = ref(false);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  // schedule only required for normal (non-trigger) campaigns
  scheduledAt: { required: requiredIf(() => !state.triggerEnabled) },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const segmentList = computed(() =>
  segments.value.map(segment => ({
    value: segment.id,
    label: `${segment.name} (${segment.contacts_count})`,
  }))
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience:
    submitAttempted.value && !state.triggerEnabled && !hasAudience.value
      ? 'Select at least one label or one segment'
      : '',
  trigger:
    submitAttempted.value &&
    state.triggerEnabled &&
    completeTriggerConditions.value.length === 0
      ? 'Add at least one trigger condition'
      : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !audienceSatisfied.value ||
    !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  submitAttempted.value = false;
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

// Builds the template_params payload (name/namespace/category/language/processed_params).
// Shared by the real campaign submit and the "Send test message" action so a test
// renders identically to the published message.
const buildTemplateParams = () => {
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;
  return {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };
};

const prepareCampaignDetails = () => {
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  const templateParams = buildTemplateParams();

  const details = {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: state.triggerEnabled
      ? null
      : formatToUTCString(state.scheduledAt),
    audience: state.triggerEnabled
      ? []
      : [
          ...(state.selectedAudience?.map(id => ({ id, type: 'Label' })) || []),
          ...(state.selectedSegments?.map(id => ({
            id,
            type: 'Segment',
          })) || []),
        ],
  };

  if (state.triggerEnabled) {
    // live campaign: fires when a new company matches these conditions
    details.trigger_rules = {
      event: 'company_created',
      match_type: state.triggerMatchType,
      conditions: completeTriggerConditions.value.map(c => ({
        source: 'company',
        key: c.key,
        operator: c.operator,
        value: needsValue(c.operator) ? c.value : '',
      })),
    };
  }

  return details;
};

const handleSubmit = async () => {
  submitAttempted.value = true;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !audienceSatisfied.value) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// ---- Send test message -------------------------------------------------
const showTestPanel = ref(false);
const testPhoneNumber = ref('');
const isSendingTest = ref(false);

// can test once an inbox + template are chosen
const canSendTest = computed(() => !!state.inboxId && !!state.templateId);

const toggleTestPanel = () => {
  showTestPanel.value = !showTestPanel.value;
};

const sendTestMessage = async () => {
  if (!testPhoneNumber.value) {
    useAlert('Please enter a recipient phone number.');
    return;
  }
  isSendingTest.value = true;
  try {
    const { data } = await CampaignsAPI.sendTest({
      inboxId: state.inboxId,
      templateParams: buildTemplateParams(),
      phoneNumber: testPhoneNumber.value,
    });
    if (data?.success) {
      useAlert('Test message sent. Check the recipient device.');
      showTestPanel.value = false;
      testPhoneNumber.value = '';
    } else {
      useAlert(data?.error || 'Could not send the test message.');
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Could not send the test message.'
    );
  } finally {
    isSendingTest.value = false;
  }
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <!-- Trigger (optional): turn this into a live campaign fired by a new company -->
    <div class="flex flex-col gap-2 rounded-lg border border-n-weak p-3">
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="state.triggerEnabled"
          type="checkbox"
          class="size-4 rounded accent-woot-500"
        />
        <span class="text-sm font-medium text-n-slate-12">
          Trigger (send automatically when a new company matches)
        </span>
      </label>
      <p class="text-xs text-n-slate-11">
        When enabled, this campaign stays live and is sent automatically to all
        contacts of every new company that matches the conditions below (once per
        company). Audience and schedule are not needed.
      </p>

      <div v-if="state.triggerEnabled" class="flex flex-col gap-3 mt-1">
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          Match
          <div class="flex rounded-lg overflow-hidden border border-n-weak">
            <button
              type="button"
              class="px-2.5 py-0.5 text-sm"
              :class="state.triggerMatchType === 'all' ? 'bg-woot-500 text-white' : 'bg-n-alpha-1 text-n-slate-11'"
              @click="state.triggerMatchType = 'all'"
            >
              All
            </button>
            <button
              type="button"
              class="px-2.5 py-0.5 text-sm"
              :class="state.triggerMatchType === 'any' ? 'bg-woot-500 text-white' : 'bg-n-alpha-1 text-n-slate-11'"
              @click="state.triggerMatchType = 'any'"
            >
              Any
            </button>
          </div>
          of the conditions
        </div>

        <div
          v-for="(condition, index) in state.triggerConditions"
          :key="index"
          class="flex items-center gap-2 flex-wrap"
        >
          <button
            type="button"
            class="p-1 rounded-full text-n-ruby-11 hover:bg-n-ruby-3 shrink-0"
            @click="removeTriggerCondition(index)"
          >
            <span class="i-lucide-circle-minus size-4" />
          </button>
          <select
            v-model="condition.key"
            class="h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none w-48 !mb-0"
          >
            <option
              v-for="option in companyPropertyOptions"
              :key="option.key"
              :value="option.key"
            >
              {{ option.label }}
            </option>
          </select>
          <select
            v-model="condition.operator"
            class="h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none w-40 !mb-0"
          >
            <option
              v-for="op in TRIGGER_OPERATORS"
              :key="op.value"
              :value="op.value"
            >
              {{ op.label }}
            </option>
          </select>
          <template v-if="needsValue(condition.operator)">
            <select
              v-if="triggerListOptions(condition)"
              v-model="condition.value"
              class="h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none w-48 !mb-0"
            >
              <option value="" disabled>— Select —</option>
              <option
                v-for="opt in triggerListOptions(condition)"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
            <input
              v-else
              v-model="condition.value"
              type="text"
              placeholder="Value"
              class="h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none w-48 !mb-0"
            />
          </template>
        </div>

        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 w-fit"
          @click="addTriggerCondition"
        >
          <span class="i-lucide-circle-plus size-4" />
          Add condition
        </button>
        <p v-if="formErrors.trigger" class="text-xs text-n-ruby-11">
          {{ formErrors.trigger }}
        </p>
      </div>
    </div>

    <template v-if="!state.triggerEnabled">
      <div class="flex flex-col gap-1">
        <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="state.selectedAudience"
          :options="audienceList"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
          :has-error="!!formErrors.audience"
          :message="formErrors.audience"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <div v-if="segmentList.length" class="flex flex-col gap-1">
        <label for="segments" class="mb-0.5 text-sm font-medium text-n-slate-12">
          Segments
        </label>
        <TagMultiSelectComboBox
          v-model="state.selectedSegments"
          :options="segmentList"
          label="Segments"
          placeholder="Select segments"
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          The campaign will be sent to contacts in the selected segments and labels combined (no duplicates).
        </p>
      </div>

      <Input
        v-model="state.scheduledAt"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
        type="datetime-local"
        :min="currentDateTime"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
        :message="formErrors.scheduledAt"
        :message-type="formErrors.scheduledAt ? 'error' : 'info'"
      />
    </template>

    <!-- Send test message: send this exact template to one number before publishing -->
    <div
      v-if="canSendTest"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">Test this message</span>
        <button
          type="button"
          class="text-sm text-n-blue-text hover:underline"
          @click="toggleTestPanel"
        >
          {{ showTestPanel ? 'Hide' : 'Send test message' }}
        </button>
      </div>
      <template v-if="showTestPanel">
        <p class="text-xs text-n-slate-11">
          Sends this exact template (image, buttons and the values you entered) to
          one number so you can check it before publishing to the whole audience.
        </p>
        <PhoneNumberInput
          v-model="testPhoneNumber"
          placeholder="Recipient mobile number"
        />
        <Button
          type="button"
          label="Send test"
          class="w-fit"
          :is-loading="isSendingTest"
          :disabled="isSendingTest || !testPhoneNumber"
          @click="sendTestMessage"
        />
      </template>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
